import { Box } from "@/components/ui/box";
import { Text } from "@/components/ui/text";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "@/components/ui/checkbox";
import { CheckIcon, CloseIcon, Icon } from "@/components/ui/icon";
import { Link as ExpoLink, useRouter } from "expo-router";
import {
  ComponentPropsWithoutRef,
  ReactNode,
  forwardRef,
  useCallback,
  useState,
} from "react";
import React from "react";
import { Pressable } from "@/components/ui/pressable";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Heading } from "@/components/ui/heading";
import { VStack } from "@/components/ui/vstack";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { ExternalLink } from "lucide-react-native";
import {
  Kycdto,
  UserDTOKycItem,
  UserDTOKycItemStatus,
} from "@/generated/api/models";
import {
  useUserControllerMe,
  useUserKYCControllerChallengeKyc,
} from "@/generated/api/api";
import { Platform } from "react-native";
import { AppStoreButtons } from "./app.store.buttons";

export type ActionItemType =
  | "TIPS_RESTRICTED"
  | "PAYOUT_RESTRICTED"
  | "KYC"
  | "BENEFICIARY_UPDATE"
  | "PROFILE"
  | "CREATE_JOIN_RESTAURANT"
  | "DOWNLOAD_APP";

export const ActionItems = {
  BENEFICIARY_UPDATE: function BeneficiaryUpdate({
    status,
  }: {
    status: "PENDING" | "DECLINED";
  }) {
    return (
      <ExpoLink href={"/(tabs)/profile"} replace>
        {status === "PENDING" ? (
          <ActionItem
            label="Verify bank account"
            sublabel="Please upload document in Profile, to verify your bank account"
          />
        ) : status === "DECLINED" ? (
          <ActionItem
            label="Verify bank account"
            sublabel="Document has been rejected, please check details in Profile"
          />
        ) : null}
      </ExpoLink>
    );
  },
  PROFILE: function Profile() {
    return (
      <ExpoLink href="/(tabs)/profile#name" replace>
        <ActionItem
          label="Complete registration"
          sublabel="We just need your first and the last name."
        />
      </ExpoLink>
    );
  },
  DOWNLOAD_APP: function DownloadApp() {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    return (
      <>
        <Pressable onPress={() => setModalIsOpen(true)}>
          <ActionItem
            label="📲 Continue in the app"
            sublabel="Instant access to tips right from your pocket"
          />
        </Pressable>
        <Modal
          isOpen={modalIsOpen}
          onClose={() => {
            setModalIsOpen(false);
          }}
          size="md"
        >
          <ModalBackdrop />
          <ModalContent>
            <ModalHeader>
              <Heading size="md" className="text-typography-950">
                📲 Continue in the app
              </Heading>
              <ModalCloseButton>
                <Icon
                  as={CloseIcon}
                  size="md"
                  className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
                />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <VStack className="gap-4">
                <Text>Instant access to tips right from your pocket</Text>
                <AppStoreButtons className="flex flex-col sm:flex-row gap-4 self-center mt-5" />
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  },
  CREATE_JOIN_RESTAURANT: function CreateJoinRestaurant() {
    const { data: user } = useUserControllerMe();

    return (
      <ExpoLink href={"/(tabs)/orgs"} replace>
        <ActionItem
          label="Add or join business"
          sublabel={`To get started, add a business or ask the team manager to send you an invitation link or email${
            user?.email ? ` to ${user.email}` : ""
          }`}
        />
      </ExpoLink>
    );
  },
  KYC: function KYC({ challenge }: { challenge: UserDTOKycItem | Kycdto }) {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const {
      mutate,
      data: challengedKYC,
      isPending,
    } = useUserKYCControllerChallengeKyc();
    const router = useRouter();

    const verificationURL = challengedKYC?.url ?? challenge.url;
    const onVerification = useCallback(
      (url: string | undefined) => {
        if (url) {
          if (Platform.OS === "web") {
            window.open(url, "_blank");
          } else {
            router.navigate(url as any);
          }
        }
      },
      [router]
    );

    return (
      <>
        <Pressable onPress={() => setModalIsOpen(true)}>
          <ActionItem
            label="Verify identity"
            sublabel="To make payouts and tipping work seamlessly, please tap to proceed with identity verification"
          />
        </Pressable>
        <Modal
          isOpen={modalIsOpen}
          onClose={() => {
            setModalIsOpen(false);
          }}
          size="md"
        >
          <ModalBackdrop />
          <ModalContent>
            <ModalHeader>
              <Heading size="md" className="text-typography-950">
                Identity verification
              </Heading>
              <ModalCloseButton>
                <Icon
                  as={CloseIcon}
                  size="md"
                  className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
                />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <VStack className="gap-4">
                <Text>
                  To keep your account secure and ensure compliance with
                  financial regulations, we require identity verification. This
                  quick process helps us protect your funds and enable seamless
                  transactions. Please complete the verification to continue
                  enjoying all features of our service.
                </Text>
                <Text size="sm">
                  For payouts we use infrastructure of{" "}
                  <Text size="sm" bold>
                    {challenge.providerName}
                  </Text>{" "}
                  - trusted financial institution, and you will be verified on
                  their website.
                </Text>
                <Text size="sm">
                  Once your application is finished, it may take some time for
                  review. Thanks for your patience.
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                action="secondary"
                onPress={() => {
                  setModalIsOpen(false);
                }}
              >
                <ButtonText>Cancel</ButtonText>
              </Button>
              <Button
                onPress={() => {
                  if (verificationURL) {
                    onVerification(verificationURL);
                  } else {
                    mutate(
                      { challengeId: challenge.id },
                      { onSuccess: (data) => onVerification(data.url) }
                    );
                  }
                }}
              >
                <ButtonIcon as={ExternalLink} />
                <ButtonText>Verify identity</ButtonText>
                {isPending && <ButtonSpinner />}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  },
  TIPS_RESTRICTED: function TipsRestricted() {
    return (
      <ActionItem
        label="Tips has been restricted"
        sublabel="This could be temporary measure due to detected fraudulent activity or missing verifications from your side. Please, contact our support for more information"
        isInvalid
      />
    );
  },
  PAYOUT_RESTRICTED: function PayoutsRestricted() {
    return (
      <ActionItem
        label="Payouts has been restricted"
        sublabel="This could be temporary measure due to detected fraudulent activity or missing verifications from your side. Please, contact our support for more information"
        isInvalid
      />
    );
  },
} as const satisfies { [key in ActionItemType]: any };

export function ActionItem({
  label,
  sublabel,
  ...rest
}: {
  label: string;
  sublabel?: ReactNode;
} & Pick<
  ComponentPropsWithoutRef<typeof Checkbox>,
  "isDisabled" | "isIndeterminate" | "isChecked" | "isInvalid"
>) {
  return (
    <Box>
      <Checkbox isChecked={false} value={label} {...rest}>
        <CheckboxIndicator>
          <CheckboxIcon as={CheckIcon} />
        </CheckboxIndicator>
        <CheckboxLabel className="text-typography-700">{label}</CheckboxLabel>
      </Checkbox>
      {typeof sublabel === "string" ? (
        <Text size="sm" className="ml-7 text-typography-600">
          {sublabel}
        </Text>
      ) : (
        sublabel
      )}
    </Box>
  );
}

for (const [key, value] of Object.entries(ActionItems)) {
  Object.assign(value, {
    actionType: key,
  });
}
